/**
 * PostHog product analytics plugin (browser-side).
 *
 * This wires posthog-js into the Vue app and the Vue Router so that:
 *   - posthog.init() is called exactly once at app startup
 *   - every SPA route change emits a $pageview event (autocapture alone
 *     does not catch client-side route changes in a SPA)
 *   - if no key is configured (VUE_APP_POSTHOG_KEY empty) the plugin is a
 *     hard no-op, so local `npm run serve` doesn't pollute the dashboard
 *
 * Config (Vue CLI inlines these env vars at BUILD time, not runtime):
 *   VUE_APP_POSTHOG_KEY   - PostHog project key (phc_...). PUBLIC by design.
 *   VUE_APP_POSTHOG_HOST  - PostHog ingestion host. Defaults to US cloud.
 *
 * Usage in any component:
 *   this.$posthog.capture('event_name', { foo: 'bar' });
 */
import posthog from 'posthog-js';

let initialized = false;
const noopAnalytics = {
  capture() {},
  identify() {},
  reset() {},
};

export function installPosthog(app, router) {
  const key = process.env.VUE_APP_POSTHOG_KEY;
  const host = process.env.VUE_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (!key) {
    app.config.globalProperties.$posthog = noopAnalytics;
    // Dev mode (or misconfigured prod) — do not initialize.
    // eslint-disable-next-line no-console
    console.info('[posthog] VUE_APP_POSTHOG_KEY not set, analytics disabled');
    return;
  }

  app.config.globalProperties.$posthog = posthog;

  if (initialized) return;
  initialized = true;

  posthog.init(key, {
    api_host: host,
    // `defaults: '<YYYY-MM-DD>'` pins the SDK's default options to a known
    // release date so future SDK upgrades don't silently change behaviour.
    // See https://posthog.com/docs/libraries/js for the matrix.
    defaults: '2026-05-30',
    // We send pageviews manually via the router hook below, so disable the
    // built-in capture to avoid duplicate events on first paint of an SPA.
    capture_pageview: false,
  });

  if (router && typeof router.afterEach === 'function') {
    router.afterEach((to) => {
      // Use the resolved fullPath so query-string changes (e.g. /ideas?folder=3)
      // are tracked as separate pageviews — useful for filter analytics.
      posthog.capture('$pageview', {
        $current_url: window.location.origin + to.fullPath,
      });
    });
  }
}

export default posthog;
